import {
  datadogRum,
  RumEvent,
  RumEventDomainContext,
} from '@datadog/browser-rum';
import { IAppConfig } from 'config/appConfig/appConfig.types';
import { IAnalyticUserInfo } from 'contexts/types/analytics';
import { parse } from 'graphql';

/**
 * Enrich events being sent to DataDog.
 *
 * @param {RumEvent} event - The RUM event.
 * @param {RumEventDomainContext} context - The RUM event domain context.
 * @returns {boolean} - Whether the event should be sent to DataDog.
 */
const eventEnrichment = (
  event: RumEvent,
  context: RumEventDomainContext
): boolean => {
  if (
    event.context &&
    'response' in context &&
    context.response &&
    event.type === 'resource' &&
    event.resource.type === 'fetch'
  ) {
    const { url, status_code: statusCode } = event.resource;

    // Don't track invalid password requests as errors
    if (
      url.endsWith('/auth/oauth/v2/enterprise/portals/password/auth') &&
      statusCode === 401
    ) {
      return false;
    }

    // Extract the traceId from the response headers for all fetch requests
    event.context.response = {
      traceId: context.response.headers.get('x-trace-id') || '',
    };

    // Check if GraphQL request is being called
    let body = '';
    if (context.requestInit?.body) {
      body =
        typeof context.requestInit.body === 'string'
          ? context.requestInit.body
          : JSON.stringify(context.requestInit.body);
    }

    if (body.includes('query') || body.includes('mutation')) {
      // Parse the body to get details about the request for enriching the event
      const { query } = JSON.parse(body);

      if (typeof query === 'string') {
        const ast = parse(query);
        const definition = ast.definitions[0] as any;
        const isQueryCall = definition.operation === 'query';
        const gqlOperationName = definition.name.value;

        event.context.request = {
          type: isQueryCall ? 'query' : 'mutation',
          name: gqlOperationName,
        };
      }
    }
  }

  return true;
};

/**
 * Initializes the DataDog service.
 *
 * @param {IAppConfig} config - The application configuration.
 * @returns {void}
 */
export const init = (config: IAppConfig) => {
  const { app, dataDog } = config;

  if (!dataDog.enabled) {
    return;
  }

  datadogRum.init({
    applicationId: dataDog.applicationId,
    clientToken: dataDog.clientToken,
    site: dataDog.site,
    service: dataDog.service,
    env: dataDog.env,
    version: app.versionName,
    sessionSampleRate: dataDog.sessionSampleRate || 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    enableExperimentalFeatures: ['feature_flags'],
    traceSampleRate: dataDog.traceSampleRate || 100,
    allowedTracingUrls: [
      new RegExp(`^${config.api.graphql}.*$`),
      new RegExp(`^${config.api.restEndpoint}.*$`),
    ],
    beforeSend: eventEnrichment,
  });

  datadogRum.startSessionReplayRecording();

  // Add global context for team tagging and cost attribution
  datadogRum.setGlobalContextProperty('team', 'xdg-enterprise');
  datadogRum.setGlobalContextProperty('app_id', 'APP5364');
  datadogRum.setGlobalContextProperty('app_ref_id', 'opscp');
};

/**
 * Sets the user information for DataDog.
 *
 * @param {IAuthUser} authUser - The authenticated user.
 * @returns {void}
 */
export const setUser = (authUser: IAnalyticUserInfo) => {
  datadogRum.setUser({
    id: authUser.userId,
    organizationId: authUser.organizationId,
    organizationName: authUser.organizationName,
    isInternal: authUser.isInternal,
    isPID: authUser.isPID,
    isPremier: authUser.isPremier,
  });
};

/**
 * Clears the DataDog user session.
 *
 * @returns {void}
 */
export const clearUserSession = () => {
  datadogRum.clearUser();
};
